import React, { useMemo } from 'react';
import { Layout, Menu, Switch, Spin, Drawer } from 'antd';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useResponsive, useMobileMenu } from '../../Hooks/useContext';
import usePermissionCache from '../../Hooks/usePermissionCache';
import { usePermissions } from '../../Hooks/usePermissions';
import { getVisibleMenuItems, MenuItem } from '../../Utilis/menuConfig';
import useCompany from '../../Hooks/company';
import { CatalogIcon } from '../../icons/catalog';
import { SettingIcon } from '../../icons/SettingIcon';
import { OrderIcon } from '../../icons/OrderICon';
import { ActivityIcon } from '../../icons/ActivityIcon';
import { ECommerceIcon } from '../../icons/ECommerceIcon';
import { LocationIcon } from '../../icons/LocationIcon';
import { HomeIcon } from '../../icons/HomeIcon';
import { OffersIcon } from '../../icons/OffersIcon';
import { LogsIcon } from '../../icons/LogsIcon';
import { IntegrationsIcon } from '../../icons/IntegrationsIcon';
import { ReportIcon } from '../../icons/ReportIcon';
import { DMSSettingsIcon } from '../../icons/DMSSettingsIcon';
import { PurchasingIcon } from '../../icons/PurchasingIcon';
import { PagesIcon } from '../../icons/PagesIcon';
import { InsuranceIcon } from '../../icons/InsuranceIcon';
import { ShortLinkIcon } from '../../icons/ShortLinkIcon';
import { TeamOutlined, PushpinOutlined } from '@ant-design/icons';
import { useTheme } from '../../Hooks/useTheme';
import { ImageWithChecker } from '../ImageChecker';
import MenuLabel from './MenuLabel';
import { SUBMENU_ICON_MAP, SUBMENU_FALLBACK_ICON } from './submenuIcons';
import { isNewTabClick, useOpenRoute, useRouteLink } from '../../Hooks/useRouteLink';
import './style.scss';

const { Sider } = Layout;

/** Inbox theme `.side`: 229px with labels, a 64px icon rail when unpinned. */
const SIDER_WIDTH_OPEN = 229;
const SIDER_WIDTH_RAIL = 64;
/** The pin survives reloads; first visit starts pinned, as the theme shows it. */
const PIN_STORAGE_KEY = 'sidebar-pinned';

const readPinned = (): boolean => {
  try {
    return localStorage.getItem(PIN_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

// Keys of the submenus leading to the leaf that owns `pathname`, outermost
// first. Exact path matches win; otherwise the longest leaf path the URL sits
// under (segment-wise, so /users/customize-columns maps to /users).
const findOpenTrail = (items: MenuItem[], pathname: string): string[] => {
  let bestTrail: string[] = [];
  let bestScore = -1;
  const walk = (entries: MenuItem[], parents: string[]) => {
    for (const entry of entries) {
      if (entry.children?.length) {
        walk(entry.children, [...parents, entry.key]);
      } else if (entry.path && entry.path !== '/') {
        const score =
          pathname === entry.path
            ? Infinity
            : pathname.startsWith(`${entry.path}/`)
              ? entry.path.length
              : -1;
        if (score > bestScore) {
          bestScore = score;
          bestTrail = parents;
        }
      }
    }
  };
  walk(items, []);
  return bestTrail;
};

// Icon mapping for menu items
const ICON_MAP: { [key: string]: React.ComponentType<any> } = {
  home: HomeIcon,
  reports: ReportIcon,
  catalog: CatalogIcon,
  orders: OrderIcon,
  activities: ActivityIcon,
  configuration: SettingIcon,
  eCommerce: ECommerceIcon,
  settings: SettingIcon,
  companies: TeamOutlined,
  locations: LocationIcon,
  logs: LogsIcon,
  integrations: IntegrationsIcon,
  'dms-settings': DMSSettingsIcon,
  purchasing: PurchasingIcon,
  pages: PagesIcon,
  insurance: InsuranceIcon,
  'short-links': ShortLinkIcon,
};

export const PermissionSideMenu: React.FC = () => {
  const { isDesktop, isMobile } = useResponsive();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMobileMenu();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [alwaysOpen, setAlwaysOpen] = useState(readPinned);
  const openKeysRef = useRef<string[]>([]);
  const sideBarRef = useRef<any>(null);

  // Cache drives loading / admin bootstrap; live Redux permissions drive visibility
  // so the sidebar drops items as soon as auth/me no longer includes them.
  const { isLoading, cachedPermissions } = usePermissionCache();
  const {
    hasAnyPermission,
    hasAllPermissions,
    userType,
  } = usePermissions();
  const { company } = useCompany();
  const location = useLocation();
  const routeLink = useRouteLink();
  const openRoute = useOpenRoute();
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const appVersion =
    (import.meta as any)?.env?.VITE_APP_VERSION ||
    (import.meta as any)?.env?.REACT_APP_VERSION ||
    '1.0';

  // Get visible menu items based on live permissions from auth/me (Redux)
  const visibleMenuItems = useMemo(() => {
    if (isLoading || !cachedPermissions) {
      return [];
    }

    const items = getVisibleMenuItems(
      hasAnyPermission,
      company,
      userType,
      hasAllPermissions
    );
    return items;
  }, [
    isLoading,
    cachedPermissions,
    hasAnyPermission,
    hasAllPermissions,
    company,
    userType,
  ]);

  const togglePinned = () => {
    const next = !alwaysOpen;
    setAlwaysOpen(next);
    // Pinning opens the full nav; unpinning drops straight to the icon rail.
    setOpen(next);
    setOpenKeys(next ? openKeysRef.current : []);
    try {
      localStorage.setItem(PIN_STORAGE_KEY, String(next));
    } catch {
      // storage blocked: the pin just won't survive a reload
    }
  };

  // Restore the open sections once on mount when the sidebar starts pinned.
  useEffect(() => {
    if (alwaysOpen) setOpen(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close if click outside sidebar (only for desktop)
  useEffect(() => {
    if (isMobile) return;
    const handleOutSideClick = (e: MouseEvent) => {
      if (!sideBarRef.current?.contains(e.target) && !alwaysOpen) {
        setOpenKeys([]);
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handleOutSideClick);
    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [sideBarRef, alwaysOpen, isMobile]);

  // Expand the section that owns the current route, so a page opened in a new
  // tab (or reloaded) shows where it lives in the menu. Collapsed desktop
  // sidebars only remember it; the Sider restores it when expanded.
  useEffect(() => {
    const trail = findOpenTrail(visibleMenuItems, location.pathname);
    if (!trail.length) return;
    const merged = Array.from(new Set([...openKeysRef.current, ...trail]));
    openKeysRef.current = merged;
    if (isMobile || open || alwaysOpen) {
      setOpenKeys(merged);
    }
  }, [location.pathname, visibleMenuItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile menu when route changes
  useEffect(() => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname, isMobile, setIsMobileMenuOpen]);

  // Show loading spinner while permissions are loading
  if (isLoading) {
    if (isMobile) {
      return (
        <Drawer
          title={null}
          placement="left"
          onClose={() => setIsMobileMenuOpen(false)}
          open={isMobileMenuOpen}
          width={280}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin size="small" />
          </div>
        </Drawer>
      );
    }
    return (
      <Sider
        className="awfar-side-modal"
        trigger={null}
        collapsible
        width={alwaysOpen ? SIDER_WIDTH_OPEN : SIDER_WIDTH_RAIL}
        collapsed={false}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Spin size="small" />
        </div>
      </Sider>
    );
  }

  // Don't show menu if no permissions or no visible items
  if (!cachedPermissions || visibleMenuItems.length === 0) {
    return null;
  }

  // Leaf labels are real links (antd stretches a menu item's <a> over the whole
  // row) so Ctrl/Cmd/middle-click and "Open in new tab" work natively. The link
  // only supplies the href; navigation stays in handleItemClick so keyboard
  // activation of the item keeps working too.
  const renderLabel = (entry: MenuItem) => {
    const label = <MenuLabel text={t(entry.title)} />;
    if (!entry.path) return label;
    return (
      <a
        href={routeLink(entry.path).href}
        onClick={(event) => {
          if (!isNewTabClick(event)) event.preventDefault();
        }}
      >
        {label}
      </a>
    );
  };

  const handleItemClick = (
    entry: MenuItem,
    domEvent: React.MouseEvent | React.KeyboardEvent
  ) => {
    if (!entry.path) return;
    // A modified mouse click already opened the link's href in a new tab.
    if (domEvent.type === 'click' && isNewTabClick(domEvent)) return;
    openRoute(entry.path, domEvent);
    if (isMobile && !isNewTabClick(domEvent)) {
      setIsMobileMenuOpen(false);
    }
  };

  // Child rows take their icon from SUBMENU_ICON_MAP (antd icons, which must
  // not receive the custom icons' `isActive` prop — it would land on the DOM).
  const renderChildIcon = (entry: MenuItem, isActive: boolean) => {
    if (entry.icon) return <entry.icon isActive={isActive} />;
    const SubIcon = SUBMENU_ICON_MAP[entry.key] || SUBMENU_FALLBACK_ICON;
    return <SubIcon />;
  };

  // Render child menu items recursively
  const renderMenuItems = (items: MenuItem[]): React.ReactNode => {
    return items.map(item => {
      const isActive = item.path ? location.pathname === item.path : false;

      if (item.children && item.children.length > 0) {
        return (
          <Menu.SubMenu
            key={item.key}
            title={<MenuLabel text={t(item.title)} />}
            icon={renderChildIcon(item, isActive)}
            className="menu-setting-icon stroke"
          >
            {renderMenuItems(item.children)}
          </Menu.SubMenu>
        );
      }

      return (
        <Menu.Item
          key={item.key}
          className={`menu-setting-icon ${isActive ? 'active' : ''}`}
          onClick={({ domEvent }) => handleItemClick(item, domEvent)}
          icon={renderChildIcon(item, isActive)}
        >
          {renderLabel(item)}
        </Menu.Item>
      );
    });
  };

  // Special render function for Activities menu with Configuration separator
  const renderActivitiesMenu = (items: MenuItem[]): React.ReactNode => {
    return items.map((item) => {
      const IconComponent = item.icon || ICON_MAP[item.key];
      const isActive = item.path ? location.pathname === item.path : false;

      if (item.children && item.children.length > 0) {
        return (
          <Menu.SubMenu
            key={item.key}
            title={<MenuLabel text={t(item.title)} />}
            icon={IconComponent ? <IconComponent isActive={isActive} /> : null}
            className="menu-setting-icon stroke"
          >
            {item.key === 'activities' ? (
              <>
                {/* Render All Tickets first */}
                {item.children.map((child: MenuItem) => {
                  if (child.key === 'allTickets') {
                    const isChildActive = child.path ? location.pathname === child.path : false;
                    return (
                      <Menu.Item
                        key={child.key}
                        className={`menu-setting-icon ${isChildActive ? 'active' : ''}`}
                        onClick={({ domEvent }) => handleItemClick(child, domEvent)}
                        icon={renderChildIcon(child, isChildActive)}
                      >
                        {renderLabel(child)}
                      </Menu.Item>
                    );
                  }
                  return null;
                })}
                
                {/* Add Configuration separator after All Tickets */}
                <Menu.Item
                  key="activities-configuration-separator"
                  className="configuration-text"
                  disabled
                >
                  {t("sideBar.configuration", "Configuration")}
                </Menu.Item>
                
                {/* Render remaining activity items */}
                {item.children.map((child: MenuItem) => {
                  if (child.key !== 'allTickets') {
                    const isChildActive = child.path ? location.pathname === child.path : false;
                    return (
                      <Menu.Item
                        key={child.key}
                        className={`menu-setting-icon ${isChildActive ? 'active' : ''}`}
                        onClick={({ domEvent }) => handleItemClick(child, domEvent)}
                        icon={renderChildIcon(child, isChildActive)}
                      >
                        {renderLabel(child)}
                      </Menu.Item>
                    );
                  }
                  return null;
                })}
              </>
            ) : (
              renderMenuItems(item.children)
            )}
          </Menu.SubMenu>
        );
      }

      return (
        <Menu.Item
          key={item.key}
          className={`menu-setting-icon ${isActive ? 'active' : ''}`}
          onClick={({ domEvent }) => handleItemClick(item, domEvent)}
          icon={IconComponent ? <IconComponent /> : null}
        >
          {renderLabel(item)}
        </Menu.Item>
      );
    });
  };

  const companyInfo = company?.company_information;
  const companyLogo = i18n.language === 'ar' ? companyInfo?.logo_ar : companyInfo?.logo;

  // Theme `.logorow`: product logo, hairline, client logo slot
  const logoRow = (
    <div className="side-logo-row">
      <img
        className="side-logo"
        src={isDark ? '/assets/awfarLogo.webp' : '/assets/awfarLogoColor.webp'}
        alt="Awfar"
      />
      <span className="side-logo-divider" aria-hidden="true" />
      {companyLogo ? (
        <span className="side-client-logo">
          <ImageWithChecker
            imageUrl={companyLogo}
            defaultIcon="/assets/logoColored.webp"
            useSrc={true}
          />
        </span>
      ) : (
        <span className="side-client-placeholder" title={companyInfo?.name_ar || companyInfo?.name_en}>
          {companyInfo?.name_ar || companyInfo?.name_en || t('sideBar.clientLogo', 'Client logo')}
        </span>
      )}
    </div>
  );

  const menuContent = (
    <div className="menu-wrapper">
      {logoRow}

      <Menu
        openKeys={openKeys}
        onOpenChange={(keys) => {
          setOpenKeys(keys);
          openKeysRef.current = keys;
        }}
        mode="inline"
        inlineIndent={12}
        defaultSelectedKeys={["1"]}
      >
        {renderActivitiesMenu(visibleMenuItems)}
      </Menu>

      {/* Theme: the bordered button at the foot of the nav */}
      <div className="side-footer">
        {!isMobile && (
          <div
            className="always-open-switch"
            data-tooltip={t("sideBar.alwaysOpen", "Pin Sidebar")}
            onClick={(event) => {
              event.stopPropagation();
              togglePinned();
            }}
          >
            <PushpinOutlined className="always-open-icon" rotate={alwaysOpen ? 0 : -45} />
            <span>{t("sideBar.alwaysOpen", "Pin Sidebar")}</span>
            <Switch size="small" checked={alwaysOpen} />
          </div>
        )}
        <div className="version-display">
          <span>v{appVersion}</span>
        </div>
      </div>
    </div>
  );

  // Mobile drawer version
  if (isMobile) {
    return (
      <Drawer
        title={null}
        placement="left"
        onClose={() => setIsMobileMenuOpen(false)}
        open={isMobileMenuOpen}
        width={280}
        bodyStyle={{ padding: 0 }}
        className="mobile-side-menu-drawer"
      >
        <div ref={sideBarRef} className="awfar-side-modal mobile open">
          {menuContent}
        </div>
      </Drawer>
    );
  }

  // Desktop version
  return (
    <Sider
      className={`awfar-side-modal ${open || alwaysOpen ? "open" : ""}`}
      trigger={null}
      collapsible
      width={open || alwaysOpen ? SIDER_WIDTH_OPEN : SIDER_WIDTH_RAIL}
      collapsed={false}
      onClick={() => {
        setOpenKeys(openKeysRef.current);
        setOpen(true);
      }}
      ref={sideBarRef}
    >
      {menuContent}
    </Sider>
  );
};

export default PermissionSideMenu; 